/**
 * ErrorBoundary — last-resort safety net for render-time failures.
 *
 * Why a class component (and not a hook):
 *   As of React 19 the error-boundary lifecycle hooks
 *   (`getDerivedStateFromError` + `componentDidCatch`) are still
 *   only available on class components. There is no
 *   `useErrorBoundary` in core React; the React team has
 *   deliberately kept catching render errors out of the hooks API
 *   because the render-throw protocol is fundamentally a class-
 *   lifecycle concern. Any function-component `try/catch` only
 *   catches errors thrown synchronously inside the function body —
 *   it does NOT catch errors thrown by descendants during their
 *   own render. Wrapping the entire app in this class boundary is
 *   therefore the only correct way to keep an unexpected Convex
 *   query throw, an undefined-deref in a panel, or any other
 *   render-time exception from blanking the dashboard.
 *
 * Behaviour:
 *   * Captures any error thrown by descendants during render,
 *     constructor, or lifecycle methods.
 *   * Renders a fallback panel with a sanitized message in
 *     production builds (the raw `error.message` only shows in
 *     dev) plus two recovery affordances:
 *       1. "Retry" — clears the boundary state, which causes the
 *          children to re-mount; useful when the underlying cause
 *          was a transient render-time issue (e.g. a Convex query
 *          that errored once and will succeed on resubscribe).
 *       2. "Reload page" — last-resort hard reset that bypasses
 *          any in-memory state corruption.
 *   * Telemetry: in development the error + componentStack go
 *     to `console.error` so engineers see the failure during
 *     local debugging. In production the browser console is
 *     visible to subscribers, so we stay silent there and
 *     forward the error to a registrable telemetry sink
 *     (`setErrorBoundaryTelemetry`). Default sink is a no-op
 *     until application bootstrap installs a real one
 *     (Sentry / Datadog / etc.) — same pattern as `ErrorState`.
 */
import {
  Component,
  type ErrorInfo,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";

/**
 * Telemetry sink for ErrorBoundary. Default is a no-op so the
 * production browser console stays silent. Application bootstrap
 * (e.g. main.tsx) can call `setErrorBoundaryTelemetry` to install
 * a Sentry-style sink that forwards errors server-side.
 */
type TelemetrySink = (error: Error, info: ErrorInfo) => void;
let telemetrySink: TelemetrySink = () => {};

export function setErrorBoundaryTelemetry(sink: TelemetrySink): void {
  telemetrySink = sink;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * Optional custom fallback. When provided, rendered instead of
   * the default panel; receives the captured error and the reset
   * callback so callers can build their own retry UX while still
   * inheriting the "recover by remount" behaviour.
   */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Dev only: full error to dev console so engineers see the
    // real failure during local development. The browser console
    // is subscriber-visible in prod, so we stay silent there
    // (QST-214 review pass 3, Qodo finding 'Info Exposure').
    if (!import.meta.env.PROD && typeof console !== "undefined") {
      console.error(
        "[ErrorBoundary] render error:",
        error,
        info.componentStack,
      );
    }

    // Telemetry sink ALWAYS receives the raw error + stack
    // (dev + prod). Default sink is a no-op until application
    // bootstrap installs a real one. Wrapped in try/catch so a
    // misbehaving sink can never break the boundary's own job.
    try {
      telemetrySink(error, info);
    } catch {
      /* swallow — a bad sink must never break the safety net */
    }
  }

  reset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      const { fallback } = this.props;
      if (fallback) {
        return fallback(this.state.error, this.reset);
      }
      return (
        <DefaultFallback error={this.state.error} onRetry={this.reset} />
      );
    }
    return this.props.children;
  }
}

function DefaultFallback({
  error,
  onRetry,
}: {
  error: Error;
  onRetry: () => void;
}) {
  // Production builds must not leak internals (stack traces, DB
  // table names, etc.) into the DOM. The dev-mode message is the
  // raw `error.message` so engineers debugging locally still get
  // signal; prod gets a generic recoverable message.
  const isProd = import.meta.env.PROD;
  const message = isProd
    ? "Something went wrong rendering the dashboard."
    : error.message;

  const handleReload = (e: ReactMouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  return (
    <div className="error-boundary-fallback" role="alert">
      <div className="error-boundary-card">
        <h2>Dashboard error</h2>
        <p className="error-boundary-message">{message}</p>
        <div className="error-boundary-actions">
          <button
            type="button"
            className="error-boundary-button"
            onClick={onRetry}
          >
            Retry
          </button>
          <a
            className="error-boundary-link"
            href="#reload"
            onClick={handleReload}
          >
            Reload page
          </a>
        </div>
      </div>
    </div>
  );
}
